import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UtilsService } from '../../../../shared/services/utils.service';
import { MatDividerModule } from '@angular/material/divider';
import { ManagementAuthorControllerService, ManagementAuthorResponse } from 'api-clipping';
import { take } from 'rxjs/internal/operators/take';
import { SharedService } from '../../../../shared/services/shared.service';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface InputData {
	mode: 'NEW' | 'EDIT' | 'DELETE' | 'DELETE_REASSIGN' | 'PENDING';
	type: string;
	item: ManagementAuthorResponse;
}
@Component({
	selector: 'app-author-add-edit',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		TranslateModule,
		MatCheckboxModule,
		MatDividerModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
    MatDialogModule
	],
	templateUrl: './author.component.html',
	styleUrl: './author.component.scss',
})
export class AuthorComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();

	formGroup: FormGroup = new FormGroup({});
	filteredAuthors: ManagementAuthorResponse[] | undefined;

  itemRelatedClips = 0;

  apiLoading = {
		management: false,
	};

	constructor(
		public dialogRef: MatDialogRef<AuthorComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiClippingAuthor: ManagementAuthorControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {

		switch (this.data.mode) {
			case 'NEW':
			case 'EDIT': {
				this.formGroup = this.fb.group({
					nameCyrillic: [, [Validators.required]],
					nameLatin: [, [Validators.required]],
				});

				if (this.data.item) {
					this.formGroup.patchValue(this.data.item);
				}
				break;
			}
			case 'DELETE': {
				this.data.mode = 'PENDING';
				this.getRelatedClips();

				this.formGroup = this.fb.group({
					reassignTo: [],
					reassign: [false, [Validators.required]],
				});
				break;
			}
		}
	}

	ngOnInit(): void {
		if (this.formGroup.controls['reassignTo']) {
			this.formGroup.controls['reassignTo'].valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.search(searchTerm);
			});
		}

    if (this.formGroup.controls['nameCyrillic']) {
			this.formGroup.controls['nameCyrillic'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(input => {
        this.formGroup.controls['nameLatin'].setValue(input);
				this.transliterate('nameCyrillic', 'nameLatin', 'CYR_LAT');
			});
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	private search(searchTerm: string) {
		if (searchTerm.length >= 1) {
			this.apiClippingAuthor
				.searchAuthors(searchTerm)
				.pipe(take(1))
				.subscribe(response => {
					this.filteredAuthors = response;
				});
		}
	}

	displayFn(author: ManagementAuthorResponse): string {
		return author && author.id ? this.utils.getParameterByLanguageScript(author, 'name') : '';
	}

	transliterate(currentComponentName: string, targetComponentName: string, conversion: 'CYR_LAT' | 'LAT_CYR') {
		let currentComponentValue = this.formGroup.controls[currentComponentName].value;
		if (currentComponentValue) {
			this.formGroup.controls[targetComponentName].setValue(this.utils.transliterate(conversion, currentComponentValue));
		}
	}

	action() {
    this.apiLoading.management = true;
		switch (this.data.mode) {
			case 'NEW': {
				this.createAuthor();
				break;
			}
			case 'EDIT': {
				this.editAuthor();
				break;
			}
			case 'DELETE_REASSIGN':
			case 'DELETE': {
				this.deleteAuthor();
				break;
			}
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}

  /** AUTHOR methods */
  private createAuthor() {
    this.apiClippingAuthor
    .createAuthor(this.utils.validateRequestObject(this.formGroup.value))
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.sharedService.showNotification.next({
          message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
          type: 'SUCCESS',
        });
        this.closeDialog();
      },
      error: () => {
        this.sharedService.showNotification.next({
          message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
          type: 'ERROR',
        });
        this.apiLoading.management = false;
      },
    });
  }

  private editAuthor() {
    if (this.data.item && this.data.item.id) {
      this.apiClippingAuthor
        .updateAuthor(this.utils.validateRequestObject(this.formGroup.value), this.data.item.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
              type: 'SUCCESS',
            });
            this.closeDialog();
          },
          error: () => {
            this.sharedService.showNotification.next({
              message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
              type: 'ERROR',
            });
            this.apiLoading.management = false;
          },
        });
    }
  }

  private deleteAuthor() {
    const reassignAuthor: ManagementAuthorResponse = this.formGroup.controls['reassignTo'].value;
				if (this.data.item && this.data.item.id) {
					this.apiClippingAuthor
						.deleteAuthor(this.data.item.id, reassignAuthor && reassignAuthor.id ? reassignAuthor.id : '')
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
									type: 'SUCCESS',
								});
								this.closeDialog();
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
									type: 'ERROR',
								});
                this.apiLoading.management = false;
							},
						});
				}
  }

  private getRelatedClips() {
    this.apiClippingAuthor.getAuthorRelatedToSize(this.data.item.id).pipe(take(1)).subscribe({
      next: response => {
        this.itemRelatedClips = response.relatedItemsSize || 0;
        this.data.mode = this.itemRelatedClips ? 'DELETE_REASSIGN' : 'DELETE';
      }
    })
	}
  /** (END)AUTHOR methods */



}
